import { Property, User, PropertyStatus, InvestmentStrategy, Tenant, Deal, DealStatus, MaintenanceTask, TaskPriority, TaskStatus } from '../types';
import { supabase, isSupabaseConfigured } from './supabaseClient';

// Local/demo persistence removed for cleaner SaaS.


// --- HELPERS DE LEITURA (DB -> APP) ---
const safeNum = (val: any): number => {
  if (val === null || val === undefined) return 0;
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

const safeStr = (val: any): string => (val === null || val === undefined) ? '' : String(val);

// --- HELPERS DE ESCRITA (APP -> DB) ---
const toNullable = (val: string | undefined | number | null) => {
  if (val === '' || val === undefined || val === null) return null;
  return val;
};

// --- MAPPERS ---

const mapPropertyFromDB = (p: any): Property => ({
  id: safeStr(p.id),
  address: safeStr(p.address),
  city: safeStr(p.city),
  zipCode: safeStr(p.zip_code),
  purchasePrice: safeNum(p.purchase_price),
  currentValue: safeNum(p.current_value),
  loanAmount: safeNum(p.loan_amount),
  interestRate: safeNum(p.interest_rate),
  monthlyRent: safeNum(p.monthly_rent),
  expenses: safeNum(p.expenses),
  status: (p.status as PropertyStatus) || PropertyStatus.Vacant,
  strategy: (p.strategy as InvestmentStrategy) || InvestmentStrategy.BuyAndHold,
  imageUrl: safeStr(p.image_url),
  leaseEnd: safeStr(p.lease_end),
  tenantName: safeStr(p.tenant_name),
  tenantId: safeStr(p.tenant_id),
  entityId: safeStr(p.entity_id),
  isDeleted: !!p.is_deleted,
  insuranceExpiry: safeStr(p.insurance_expiry),
  lat: p.lat,
  lng: p.lng
});

const mapPropertyToDB = (p: Partial<Property>, userId: string) => ({
  user_id: userId,
  address: p.address,
  city: p.city,
  zip_code: p.zipCode,
  purchase_price: p.purchasePrice,
  current_value: p.currentValue,
  loan_amount: p.loanAmount,
  interest_rate: p.interestRate,
  monthly_rent: p.monthlyRent,
  expenses: p.expenses,
  status: p.status,
  strategy: p.strategy,
  image_url: p.imageUrl,
  lease_end: toNullable(p.leaseEnd),
  tenant_id: toNullable(p.tenantId),
  entity_id: toNullable(p.entityId),
  insurance_expiry: toNullable(p.insuranceExpiry),
  lat: p.lat,
  lng: p.lng,
  is_deleted: p.isDeleted || false,
});

const mapTenantFromDB = (t: any): Tenant => ({
  id: safeStr(t.id),
  name: safeStr(t.name),
  email: safeStr(t.email),
  phone: safeStr(t.phone),
  nif: safeStr(t.nif),
  status: (t.status as any) || 'Active',
  rating: safeNum(t.rating),
  notes: safeStr(t.notes),
  currentPropertyId: safeStr(t.current_property_id),
  rentAmount: safeNum(t.rent_amount),
  paymentDay: safeNum(t.payment_day),
  leaseEnd: safeStr(t.lease_end),
  idNumber: safeStr(t.id_number),
  leaseStart: safeStr(t.lease_start),
  depositAmount: safeNum(t.deposit_amount)
});

const mapTenantToDB = (t: Partial<Tenant>, userId: string) => ({
  user_id: userId,
  name: t.name,
  email: t.email,
  phone: t.phone,
  nif: t.nif,
  status: t.status,
  rating: t.rating || 5,
  notes: t.notes,
  current_property_id: toNullable(t.currentPropertyId),
  rent_amount: t.rentAmount,
  payment_day: t.paymentDay,
  lease_end: toNullable(t.leaseEnd),
  lease_start: toNullable(t.leaseStart),
  id_number: t.idNumber,
  deposit_amount: t.depositAmount
});

const mapDealFromDB = (d: any): Deal => ({
  id: safeStr(d.id),
  address: safeStr(d.address),
  city: safeStr(d.city),
  askingPrice: safeNum(d.asking_price),
  offerPrice: safeNum(d.offer_price),
  estRent: safeNum(d.est_rent),
  estRenovation: safeNum(d.est_renovation),
  status: (d.status as DealStatus) || DealStatus.New,
  notes: safeStr(d.notes),
  imageUrl: safeStr(d.image_url),
  dateAdded: safeStr(d.created_at)
});

const mapDealToDB = (d: Partial<Deal>, userId: string) => ({
  user_id: userId,
  address: d.address,
  city: d.city,
  asking_price: d.askingPrice,
  offer_price: d.offerPrice,
  est_rent: d.estRent,
  est_renovation: d.estRenovation,
  status: d.status,
  notes: d.notes,
  image_url: d.imageUrl
});

// --- MAPPERS: TASKS (CRÍTICO: Deve ter safeStr e safeNum) ---
const mapTaskFromDB = (t: any): MaintenanceTask => ({
  id: safeStr(t.id),
  propertyId: safeStr(t.property_id), 
  title: safeStr(t.title),
  description: safeStr(t.description),
  priority: (t.priority as TaskPriority) || TaskPriority.Medium,
  status: (t.status as TaskStatus) || TaskStatus.Pending,
  dateCreated: safeStr(t.date_created || t.created_at), 
  costEstimate: safeNum(t.cost_estimate) 
});

const mapTaskToDB = (t: Partial<MaintenanceTask>, userId: string) => ({
  user_id: userId,
  property_id: toNullable(t.propertyId),
  title: t.title,
  description: t.description,
  priority: t.priority,
  status: t.status,
  cost_estimate: t.costEstimate,
  date_created: t.dateCreated ? t.dateCreated : new Date().toISOString() 
});

export const storageService = {
  // --- AUTH ---
  async login(email: string, password?: string): Promise<User> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password: password || '' });
    if (error) throw error;

    if (data.user) {
      return {
        id: data.user.id,
        name: data.user.user_metadata?.full_name || email.split('@')[0],
        email: data.user.email || email,
        emailVerified: !!data.user.email_confirmed_at,
        // subscription should be loaded from `profiles` later (Phase: billing)
        subscription: { plan: 'Starter', status: 'active', renewalDate: '2025-12-31', billingPeriod: 'monthly' }
      };
    }

    throw new Error('User not found');
  },

  async getCurrentUser(): Promise<User | null> {
    if (!isSupabaseConfigured()) return null;

    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;

    const u = session?.user;
    if (!u) return null;

    return {
      id: u.id,
      name: u.user_metadata?.full_name || u.email?.split('@')[0] || 'User',
      email: u.email || '',
      emailVerified: !!u.email_confirmed_at,
      subscription: { plan: 'Starter', status: 'active', renewalDate: '2025-12-31', billingPeriod: 'monthly' }
    };
  },

  async register(name: string, email: string, password: string): Promise<User> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
    }

    // IMPORTANT: with email confirmations enabled, the user may need to verify before gaining full access.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;

    const u = data.user;
    if (!u) {
      // Supabase may return null user in some edge cases.
      return {
        id: '',
        name,
        email,
        emailVerified: false,
        subscription: { plan: 'Starter', status: 'active', renewalDate: '2025-12-31', billingPeriod: 'monthly' }
      };
    }

    return {
      id: u.id,
      name,
      email: u.email || email,
      emailVerified: !!u.email_confirmed_at,
      subscription: { plan: 'Starter', status: 'active', renewalDate: '2025-12-31', billingPeriod: 'monthly' }
    };
  },

  async logout() {
    if (!isSupabaseConfigured()) return;
    await supabase.auth.signOut();
  },

  // --- Settings / legacy stubs (clean SaaS mode) ---
  getStorageUsage() {
    return 0;
  },

  async updateUserProfile(_userId: string, profile: { name?: string; email?: string }): Promise<User> {
    // Phase: implement via `profiles` table + supabase auth update.
    // For now, return current user with updated fields.
    const u = await this.getCurrentUser();
    if (!u) throw new Error('Not logged in');
    return { ...u, name: profile.name ?? u.name, email: profile.email ?? u.email };
  },

  async exportUserData(userId: string): Promise<string> {
    const data = await this.getUserData(userId);
    return JSON.stringify(data, null, 2);
  },

  async importUserData(_userId: string, _json: string): Promise<void> {
    // Clean SaaS mode: importing arbitrary JSON into DB needs validation + upserts.
    // Implement in a later phase.
    throw new Error('Import is not supported yet in clean SaaS mode.');
  },


  // --- PROPERTIES ---
  async getProperties(userId: string): Promise<Property[]> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.from('properties').select('*').eq('user_id', userId).eq('is_deleted', false).order('created_at', { ascending: false });
    if (error) { console.error("Supabase Error (Properties):", error); return []; }
    return (data || []).map(mapPropertyFromDB);
  },

  async addProperty(userId: string, property: Omit<Property, 'id'>): Promise<Property> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapPropertyToDB(property, userId);
    const newId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`;
    const { data, error } = await supabase.from('properties').insert({ ...dbPayload, id: newId }).select().single();
    if (error) throw error;
    return mapPropertyFromDB(data);
  },

  async updateProperty(userId: string, property: Property): Promise<Property> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapPropertyToDB(property, userId);
    const { error } = await supabase.from('properties').update(dbPayload).eq('id', property.id);
    if (error) throw error;
    return property;
  },

  async deleteProperty(userId: string, propertyId: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { error } = await supabase.from('properties').update({ is_deleted: true }).eq('id', propertyId);
    if (error) throw error;
  },

  // --- TENANTS ---
  async getTenants(userId: string): Promise<Tenant[]> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.from('tenants').select('*').eq('user_id', userId).order('created_at', { ascending: false });
    if (error) { console.error("Supabase Error (Tenants):", error); return []; }
    return (data || []).map(mapTenantFromDB);
  },

  async addTenant(userId: string, tenant: Omit<Tenant, 'id'>): Promise<Tenant> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapTenantToDB(tenant, userId);
    const newId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`;
    const { data, error } = await supabase.from('tenants').insert({ ...dbPayload, id: newId }).select().single();
    if (error) throw error;
    return mapTenantFromDB(data);
  },

  async updateTenant(userId: string, tenant: Tenant): Promise<Tenant> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapTenantToDB(tenant, userId);
    const { error } = await supabase.from('tenants').update(dbPayload).eq('id', tenant.id);
    if (error) throw error;
    return tenant;
  },

  async deleteTenant(userId: string, tenantId: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { error } = await supabase.from('tenants').delete().eq('id', tenantId);
    if (error) throw error;
  },

  // --- DEALS ---
  async getDeals(userId: string): Promise<Deal[]> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.from('deals').select('*').eq('user_id', userId).order('created_at', { ascending: false });
    if (error) { console.error("Supabase Error (Deals):", error); return []; }
    return (data || []).map(mapDealFromDB);
  },

  async addDeal(userId: string, deal: Omit<Deal, 'id' | 'dateAdded'>): Promise<Deal> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapDealToDB(deal as any, userId);
    const newId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`;
    const { data, error } = await supabase.from('deals').insert({ ...dbPayload, id: newId }).select().single();
    if (error) throw error;
    return mapDealFromDB(data);
  },

  async updateDeal(userId: string, deal: Deal): Promise<Deal> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapDealToDB(deal, userId);
    const { error } = await supabase.from('deals').update(dbPayload).eq('id', deal.id);
    if (error) throw error;
    return deal;
  },

  async deleteDeal(userId: string, dealId: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { error } = await supabase.from('deals').delete().eq('id', dealId);
    if (error) throw error;
  },

  // --- TASKS (BLINDAGEM APLICADA) ---
  async getTasks(userId: string): Promise<MaintenanceTask[]> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.from('maintenance_tasks').select('*').eq('user_id', userId).order('created_at', { ascending: false });
    if (error) {
      console.error("Supabase Error (Tasks):", error);
      return [];
    }
    return (data || []).map(mapTaskFromDB);
  },

  async addTask(userId: string, task: Omit<MaintenanceTask, 'id' | 'dateCreated'>): Promise<MaintenanceTask> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapTaskToDB(task as any, userId);
    const newId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`;
    const { data, error } = await supabase.from('maintenance_tasks').insert({ ...dbPayload, id: newId }).select().single();
    if (error) throw error;
    return mapTaskFromDB(data);
  },

  async updateTask(userId: string, task: MaintenanceTask): Promise<MaintenanceTask> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const dbPayload = mapTaskToDB(task, userId);
    const { error } = await supabase.from('maintenance_tasks').update(dbPayload).eq('id', task.id);
    if (error) throw error;
    return task;
  },

  async deleteTask(userId: string, taskId: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');
    const { error } = await supabase.from('maintenance_tasks').delete().eq('id', taskId);
    if (error) throw error;
  },

  // --- LEGACY ---
  async getUserData(userId: string) {
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured');

    try {
      const [propsResult, tasksResult, dealsResult, tenantsResult] = await Promise.allSettled([
        this.getProperties(userId),
        this.getTasks(userId),
        this.getDeals(userId),
        this.getTenants(userId)
      ]);

      const properties = propsResult.status === 'fulfilled' ? propsResult.value : [];
      const tasks = (tasksResult.status === 'fulfilled' ? tasksResult.value : []);
      const deals = (dealsResult.status === 'fulfilled' ? dealsResult.value : []);
      const tenants = (tenantsResult.status === 'fulfilled' ? tenantsResult.value : []);

      return { properties, tasks, deals, tenants, renovations: [], contractors: [], entities: [], invoices: [] };
    } catch (e) {
      console.error("Critical Load Error:", e);
      return { properties: [], tasks: [], deals: [], tenants: [], renovations: [], contractors: [], entities: [], invoices: [] };
    }
  },

  async saveData(_userId: string, data: any) {
    // For now, legacy save is not used in clean SaaS mode.
    // Each module should persist using its own table-specific mutations.
    return data;
  },

  getLocalData() {
    throw new Error('Local storage mode is disabled (clean SaaS mode).');
  },

  saveLocalData(_data: any) {
    throw new Error('Local storage mode is disabled (clean SaaS mode).');
  }
};